import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { checkValue, integerLength, roundValue, validate } from './utils';
import { imperativeChangeInputValue, mergeRefs } from '../utils';
import { MinusIcon, PlusIcon } from '../icons';
import styles from './styles.module.css';
import { IconButton } from '../button';
import { Input } from '../input';
/**
 * Компонент `<Counter/>` принимает количественные показатели от пользователя.
 *
 * @example
 *```jsx
 * <Counter
 *  value={state}
 *  precision={1}
 *  onChange={(e, string) => setState(string)}
 * />
 * ```
 */
export const Counter = /*#__PURE__*/forwardRef(function Counter({
  className,
  size = 'm',
  wide = false,
  value = '',
  placeholder,
  max = 999,
  min = 0,
  step = 1,
  precision = 0,
  disabled,
  onChange,
  onBlur,
  error,
  mobile,
  ...props
}, ref) {
  const validValue = useMemo(() => validate(value) || '', [value]);
  const valueToNumber = useMemo(() => +validValue, [validValue]);
  const roundedStep = useMemo(() => +roundValue(step, precision), [step, precision]);
  const inputSize = useMemo(() => integerLength(max) + precision, [max, precision]);
  const buttonSize = useMemo(() => size === 'l' ? 'l' : 'm', [size]);
  const inputRef = useRef(null);
  const changeEvent = useCallback((event, nextValue) => onChange == null ? void 0 : onChange(event, checkValue(nextValue, min, max)), [onChange, min, max]);
  const inputFocusHandler = event => event.target.select();
  const inputChangeHandler = useCallback(event => changeEvent(event, event.target.value), [changeEvent]);
  const inputBlurHandler = useCallback(event => {
    const nextValue = validValue ? roundValue(validValue, precision) : '';
    changeEvent(event, nextValue);
    onBlur == null ? void 0 : onBlur(event);
  }, [validValue, precision, changeEvent, onBlur]);
  const imperativeChangeValue = useCallback((e, number) => imperativeChangeInputValue(inputRef, e, roundValue(number, precision)), [precision, inputRef]);
  const keyDownHandler = useCallback(event => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      imperativeChangeValue(event, valueToNumber + roundedStep);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      imperativeChangeValue(event, valueToNumber - roundedStep);
    }
  }, [valueToNumber, roundedStep, imperativeChangeValue]);
  const counterClassName = useMemo(() => clsx(styles.counter, styles[`counter--${wide ? 'wide-' : ''}${size}`], className), [size, className, wide]);
  const inputClassName = useMemo(() => clsx(styles.input, styles[`input--${mobile ? 'mobile-' : ''}${size}`]), [mobile, size]);
  return /*#__PURE__*/React.createElement("div", {
    className: counterClassName,
    "data-testid": "counter"
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: buttonSize,
    disabled: valueToNumber === min || disabled,
    onClick: e => imperativeChangeValue(e, valueToNumber - step),
    "aria-label": "\u0443\u043C\u0435\u043D\u044C\u0448\u0438\u0442\u044C \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435 \u0441\u0447\u0451\u0442\u0447\u0438\u043A\u0430",
    "data-testid": "counter-decrease"
  }, /*#__PURE__*/React.createElement(MinusIcon, null)), /*#__PURE__*/React.createElement(Input, {
    ...props,
    className: inputClassName,
    inputSize: size,
    size: inputSize,
    type: "Text",
    inputMode: "decimal",
    value: validValue.replace('.', ','),
    onChange: inputChangeHandler,
    onKeyDown: keyDownHandler,
    onClick: inputFocusHandler,
    onBlur: inputBlurHandler,
    disabled: disabled,
    placeholder: placeholder,
    error: error,
    mobile: mobile,
    center: true,
    ref: mergeRefs([inputRef, ref]),
    "aria-label": "\u0441\u0447\u0451\u0442\u0447\u0438\u043A"
  }), /*#__PURE__*/React.createElement(IconButton, {
    size: buttonSize,
    disabled: valueToNumber === max || disabled,
    onClick: e => imperativeChangeValue(e, valueToNumber + step),
    "aria-label": "\u0443\u0432\u0435\u043B\u0438\u0447\u0438\u0442\u044C \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435 \u0441\u0447\u0451\u0442\u0447\u0438\u043A\u0430",
    "data-testid": "counter-increase"
  }, /*#__PURE__*/React.createElement(PlusIcon, null)));
});